import React from 'react';
import TableLoader from '../ui/Loaders/TableLoader';
import { ChevronDown, ChevronRight, FileSearch } from 'lucide-react';
import { IconTooltip } from './IconTooltip';

export interface TableColumn<T = unknown> {
  key: string;
  header: string;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

export interface Action<T> {
  text: string;
  icon: React.ReactNode;
  onClick: (row: T) => void;
}

export interface TableProps<T = unknown> {
  columns: TableColumn<T>[];
  data: T[];
  showCheckbox?: boolean;
  onSelectItem?: (item: T, checked: boolean, index: number) => void;
  onSelectAll?: (checked: boolean) => void;
  isAllSelected?: boolean;
  selectedItems?: Set<string | number>;
  getItemId?: (item: T) => string | number;
  className?: string;
  tableClassName?: string;
  minHeight?: string;
  emptyMessage?: string;
  loading?: boolean;
  actions?: Action<T>[] | ((item: T) => Action<T>[]);
  onRowClick?: (item: T) => void;
  expandable?: boolean;
  expandedRowKeys?: Set<string | number>;
  onToggleExpand?: (item: T) => void;
  renderExpandedRow?: (item: T) => React.ReactNode;
}

const CommonTable = <T extends Record<string, any>>({
  columns,
  data,
  showCheckbox = false,
  onSelectItem,
  onSelectAll,
  isAllSelected = false,
  selectedItems = new Set(),
  getItemId = (item) => item.id,
  className = '',
  tableClassName = '',
  minHeight = '',
  emptyMessage = 'No data available',
  loading = false,
  actions,
  onRowClick,

  // 🔥 Expandable props
  expandable = false,
  expandedRowKeys = new Set(),
  onToggleExpand,
  renderExpandedRow,
}: TableProps<T>) => {
  return (
    <div className={`bg-card rounded-lg border overflow-x-auto ${minHeight} ${className}`}>
      <table className={`w-full ${tableClassName}`}>

        {/* HEADER */}
        <thead>
          <tr className="border-b bg-secondary/30">

            {/* Expand Column */}
            {expandable && <th className="px-5 py-3 w-8"></th>}

            {/* Checkbox */}
            {showCheckbox && (
              <th className="px-5 py-3 text-nowrap text-left">
                {onSelectAll && (
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => onSelectAll(e.target.checked)}
                  />
                )}
              </th>
            )}

            {/* Columns */}
            {columns.map((column) => (
              <th
                key={column.key}
                className={`text-left text-xs font-medium text-muted-foreground px-5 py-3 text-nowrap ${column.headerClassName || ''}`}
              >
                {column.header}
              </th>
            ))}

            {/* Actions */}
            {actions && (
              <th className="text-center text-nowrap text-xs font-medium text-muted-foreground px-5 py-3">
                Action
              </th>
            )}
          </tr>
        </thead>

        {/* BODY */}
        <tbody>
          {loading ? (
            <TableLoader
              columns={
                columns.length +
                (actions ? 1 : 0) +
                (showCheckbox ? 1 : 0) +
                (expandable ? 1 : 0)
              }
              rows={5}
            />
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={
                  columns.length +
                  (showCheckbox ? 1 : 0) +
                  (actions ? 1 : 0) +
                  (expandable ? 1 : 0)
                }
                className="text-center py-10"
              >
                <div className="flex flex-col items-center justify-center text-muted-foreground/60">
                  <FileSearch size={40} />
                  <div className="mt-3 text-sm text-muted-foreground">
                    {emptyMessage}
                  </div>
                </div>
              </td>
            </tr>
          ) : (
            data.map((item, index) => {
              const itemId = getItemId(item);
              const isSelected = selectedItems.has(itemId);
              const isExpanded = expandedRowKeys.has(itemId);

              return (
                <React.Fragment key={`${itemId}-${index}`}>

                  {/* MAIN ROW */}
                  <tr
                    onClick={() => {
                      onRowClick?.(item);
                      if (expandable) onToggleExpand?.(item);
                    }}
                    className={`border-b last:border-b-0 transition-colors ${onRowClick || expandable
                      ? 'hover:bg-secondary/20 cursor-pointer'
                      : ''
                      }`}
                  >

                    {/* Expand Icon */}
                    {expandable && (
                      <td className="px-5 py-3">
                        {isExpanded ? <ChevronDown strokeWidth={1.5} size={18} /> : <ChevronRight strokeWidth={1.5} size={18} />}
                      </td>
                    )}

                    {/* Checkbox */}
                    {showCheckbox && (
                      <td className="px-5 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) =>
                            onSelectItem?.(item, e.target.checked, index)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}

                    {/* Data Cells */}
                    {columns.map((column) => (
                      <td
                        key={`${itemId}-${column.key}`}
                        className={`px-5 py-3 text-sm text-foreground text-nowrap ${column.className || ''}`}
                      >
                        {column.render
                          ? column.render(item, index)
                          : item[column.key]}
                      </td>
                    ))}

                    {/* Actions */}
                    {actions && (
                      <td className="px-5 py-3">
                        <div className="flex gap-2 justify-center items-center">
                          {(() => {
                            const rowActions = typeof actions === 'function' ? actions(item) : actions;
                            if (rowActions.length === 0) return <span className="text-muted-foreground opacity-50">--</span>;
                            return rowActions.map((action, i) => (
                              <IconTooltip key={i} content={action.text}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(item);
                                  }}
                                  className="p-1 rounded-md hover:bg-secondary/30 transition"
                                >
                                  {action.icon}
                                </button>
                              </IconTooltip>
                            ));
                          })()}
                        </div>
                      </td>
                    )}
                  </tr>

                  {/* EXPANDED ROW */}
                  {expandable && isExpanded && renderExpandedRow && (
                    <tr className="bg-secondary/10">
                      <td
                        colSpan={
                          columns.length +
                          (actions ? 1 : 0) +
                          (showCheckbox ? 1 : 0) +
                          1
                        }
                      >
                        {renderExpandedRow(item)}
                      </td>
                    </tr>
                  )}

                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CommonTable;